import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Tab, Tabs } from '@mui/material';

import { FBoxContainer, FHSimpleTable, FHSkeletonLoader } from '../../components/base';
import {
  facilityActions,
  clientActions,
  providerActions,
  payorActions,
  behaviorActions,
  cohortActions,
} from '../../store';
import { history, areUserRolesIn, getSortedItemsBasedOnId } from '../../helpers';
import { transformAdminTableData } from '../utils/helper';
import PageContainer from '../../components/layout/PageContainer';
import AdminTableFooter from './AdminTableFooter';
import { AdminPageTabs, Roles, SortOrder } from '../../constants';
import {
  payorColumns,
  getClientColumns,
  facilityColumns,
  getProviderColumns,
  goalTemplateColumns,
  cohortColumns,
  getEmailReportsColumns,
} from '../configs/adminDashboard.config';
import { ClinicalStatus, ProviderClinicalStatus } from '../constants/general';
import { FORTA_GRAY2, FORTA_WHITE } from '../../utils/colors';
import { useEmailReports } from './emailCronConfigs/hooks';
import { FeatureFlagsTab } from './featureFlags';

import type { RootState } from '../../store';
import type { MUIDataTableOptions } from 'mui-datatables';
import type { SyntheticEvent } from 'react';
import type { IProvider, TabPanelProps, IBehaviorGoalName, ICohort } from '../../utils/types';

const ADMIN_TAB_LOCAL_STORAGE_KEY = 'activeTab';

function AdminTableLoader() {
  return (
    <Box>
      <Box
        height={130}
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          backgroundColor: FORTA_WHITE,
        }}
      >
        <FHSkeletonLoader width={20} />
        <FHSkeletonLoader width={100} />
        <FHSkeletonLoader width={100} />
        <FHSkeletonLoader width={100} />
        <FHSkeletonLoader width={100} />
        <FHSkeletonLoader width={100} />
      </Box>
      {Array.from({ length: 10 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 3,
            borderTop: `1px solid ${FORTA_GRAY2}`,
            backgroundColor: FORTA_WHITE,
          }}
        >
          <FHSkeletonLoader width={20} />
          <FHSkeletonLoader width={100} />
          <FHSkeletonLoader width={100} />
          <FHSkeletonLoader width={100} />
          <FHSkeletonLoader width={100} />
          <FHSkeletonLoader width={100} />
        </Box>
      ))}
    </Box>
  );
}

function Dashboard() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccessTokenSilently } = useAuth0();
  const {
    list: clients = [],
    allClientsList = [],
    loading: clientLoading,
  } = useSelector((state: RootState) => state.clients);
  const { user } = useSelector((state: RootState) => state.auth);

  const {
    list: activeProviders = [],
    allProvidersList = [],
    loading: providerLoading,
  } = useSelector((state: any) => state.providers);
  const { list: facilities = [], loading: facilityLoading } = useSelector(
    (state: RootState) => state.facilities,
  );

  const { list: payorsList = [], loading: payorsLoading } = useSelector(
    (state: RootState) => state.payors,
  );

  const { allGoalTemplates = [], loading: goalNamesLoading } = useSelector(
    (state: any) => state.behaviors,
  );

  const { list: cohorts = [], loading: cohortsLoading } = useSelector(
    (state: RootState) => state.cohorts,
  );

  const {
    emailReports,
    loading: emailReportsLoading,
    fetchEmailReports,
    toggleEnabled: toggleEmailReportEnabled,
  } = useEmailReports();

  const getPersistedActiveTab = () => {
    const urlTabIndex = searchParams.get(ADMIN_TAB_LOCAL_STORAGE_KEY);
    const persistedTabIndex = localStorage.getItem(ADMIN_TAB_LOCAL_STORAGE_KEY);
    const tabIndex = urlTabIndex || persistedTabIndex;
    return tabIndex ? +decodeURIComponent(tabIndex) : 0;
  };

  const initialTabIndex = useMemo(() => {
    return getPersistedActiveTab();
  }, []);

  const [enrichedProviders, setEnrichedProviders] = useState<Array<IProvider>>([]);
  const [enrichedAllProviderList, setEnrichedAllProviderList] = useState<Array<IProvider>>([]);
  const [enrichedGoalTemplets, setEnrichedGoalTemplets] = useState<Array<IBehaviorGoalName>>([]);
  const [activeTab, setActiveTab] = useState(initialTabIndex);

  // Tab index
  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  //Handle tab change
  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    localStorage.setItem(ADMIN_TAB_LOCAL_STORAGE_KEY, String(newValue));
    setSearchParams({ [ADMIN_TAB_LOCAL_STORAGE_KEY]: encodeURIComponent(newValue) });
  };

  //Tab panel component
  function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <Box
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 0, position: 'relative' }}>{children}</Box>}
      </Box>
    );
  }

  useEffect(() => {
    switch (activeTab) {
      case AdminPageTabs.ClientTab:
        if (!clients?.length) {
          dispatch(clientActions.getList({ getAccessTokenSilently }));
        }
        break;
      case AdminPageTabs.ProviderTab:
        if (!activeProviders?.length) {
          dispatch(providerActions.getList({ getAccessTokenSilently, include_roles: true }));
        }
        break;
      case AdminPageTabs.BFTab:
        if (!facilities?.length) {
          dispatch(facilityActions.getList({ getAccessTokenSilently }));
        }
        break;
      case AdminPageTabs.PayorTab:
        if (!payorsList.length) {
          dispatch(payorActions.getList({ getAccessTokenSilently }));
        }
        break;
      case AdminPageTabs.GoalBankTab:
        if (areUserRolesIn(user?.roles, [Roles.Admin]) && !allGoalTemplates?.length) {
          dispatch(behaviorActions.getAllGoalTemplates({ getAccessTokenSilently }));
        }
        break;
      case AdminPageTabs.CohortsTab:
        if (!cohorts?.length) {
          dispatch(cohortActions.getList({ getAccessTokenSilently }));
        }
        break;
      case AdminPageTabs.EmailReportsTab:
        if (areUserRolesIn(user?.roles, [Roles.Admin]) && !emailReports?.length) {
          fetchEmailReports();
        }
        break;
      case AdminPageTabs.AllClientsTab:
        if (!allClientsList?.length) {
          dispatch(
            clientActions.getList({
              getAccessTokenSilently,
              clinical_status: [
                ClinicalStatus.CLINICAL,
                ClinicalStatus.PRE_CLINICAL,
                ClinicalStatus.OFFBOARDED,
                ClinicalStatus.ON_HOLD,
              ],
            }),
          );
        }
        break;
      case AdminPageTabs.AllProvidersTab:
        if (!allProvidersList?.length) {
          dispatch(
            providerActions.getList({
              getAccessTokenSilently,
              include_roles: true,
              clinical_status: [
                ProviderClinicalStatus.CLINICAL,
                ProviderClinicalStatus.PRE_CLINICAL,
                ProviderClinicalStatus.OFFBOARDED,
                ProviderClinicalStatus.ON_HOLD,
                ProviderClinicalStatus.LIMITED_ACCESS_GUARDIAN,
                ProviderClinicalStatus.GUARDIAN_INVITED,
              ],
            }),
          );
        }
        break;
      default:
        return;
    }
  }, [activeTab]);

  useEffect(() => {
    const active = getPersistedActiveTab();
    setSearchParams({ [ADMIN_TAB_LOCAL_STORAGE_KEY]: encodeURIComponent(active) });
    localStorage.setItem(ADMIN_TAB_LOCAL_STORAGE_KEY, String(active));
    return () => {
      dispatch(providerActions.clearList());
    };
  }, []);

  useEffect(() => {
    if (!activeProviders.length) return;

    const updatedProviders = activeProviders.map((cl: IProvider) => {
      return {
        ...cl,
        roles: cl?.roles?.join(', '),
      };
    });
    setEnrichedProviders(updatedProviders);
  }, [activeProviders]);

  useEffect(() => {
    if (!allProvidersList.length) return;

    const updatedAllProviderList = allProvidersList.map((cl: IProvider) => {
      return {
        ...cl,
        roles: cl?.roles?.join(', '),
      };
    });
    setEnrichedAllProviderList(updatedAllProviderList);
  }, [allProvidersList]);

  const updatedCohorts = cohorts.map((cohort: ICohort) => {
    return {
      ...cohort,
      service_classes: cohort?.service_classes?.join(', '),
    };
  });

  useEffect(() => {
    if (!allGoalTemplates.length) return;

    const updatedGoalTemplates = allGoalTemplates.map((goalTemplate: IBehaviorGoalName) => {
      return {
        ...goalTemplate,
        is_active: goalTemplate.is_active ? 'Yes' : 'No',
        focus_area: goalTemplate?.behavior?.label,
      };
    });
    setEnrichedGoalTemplets(updatedGoalTemplates);
  }, [allGoalTemplates]);

  const dataTableOptions: MUIDataTableOptions = {
    search: true,
    customSearch: (searchQuery, row) => {
      const trimmedQuery = searchQuery?.trim()?.toLowerCase();
      return row.some((columnValue) =>
        columnValue?.toString()?.toLowerCase()?.includes(trimmedQuery),
      );
    },
    download: true,
    print: true,
    viewColumns: true,
    filter: true,
    selectableRowsHideCheckboxes: true,
    responsive: 'standard',
    tableBodyHeight: 'auto',
    tableBodyMaxHeight: '',
    downloadOptions: {
      filterOptions: {
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true,
      },
    },
    selectableRows: 'none',
    customFooter: (
      rowCount: number,
      page: number,
      rowsPerPage: number,
      changeRowsPerPage: (page: string | number) => void,
      changePage: (newPage: number) => void,
    ) => {
      return (
        <AdminTableFooter
          count={rowCount}
          page={page}
          rowsPerPage={rowsPerPage}
          changeRowsPerPage={changeRowsPerPage}
          changePage={changePage}
        />
      );
    },
  };

  const clientOptions = {
    ...dataTableOptions,
    onRowClick: (args: any) => history.navigate(`/admin/client-details-page/${args[0]}`),
  };

  const providerOptions = {
    ...dataTableOptions,
    onRowClick: (args: any) => history.navigate(`/admin/provider/${args[0]}`),
  };

  const payorOptions = {
    ...dataTableOptions,
    onRowClick: (args: any) => {
      if (areUserRolesIn(user?.roles, [Roles.Admin, Roles.OnboardingCoordinator]))
        history.navigate(`/admin-add-payor/${args[0]}`);
    },
  };

  const goalTemplateTableOptions = {
    ...dataTableOptions,
    sortOrder: {
      name: 'id',
      direction: SortOrder.Ascending,
    },
    onRowClick: ([goalTemplateId, , , , focusAreaId]: (string | number | null)[]) =>
      history.navigate(
        `/admin/goal-bank/focus-area/${focusAreaId}/goal-template/${goalTemplateId}`,
      ),
  };

  const cohortTableOptions = {
    ...dataTableOptions,
    onRowClick: (args: any) => history.navigate(`/admin/cohort/${args[0]}`),
  };

  const emailReportsTableOptions = {
    ...dataTableOptions,
    onRowClick: (args: any) => history.navigate(`/admin/email-cron-configs/${args[0]}`),
  };

  const emailReportsColumns = getEmailReportsColumns(toggleEmailReportEnabled);

  return (
    <PageContainer>
      <Box sx={{ mt: 3 }}>
        <FBoxContainer sx={{ mt: 2, boxShadow: 'none' }} dataTestId="adminHomePageContainer">
          <Tabs
            aria-label="Tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              mb: 3,
              minHeight: 32,
              '& .MuiTab-root': {
                padding: '0 0.5rem',
                textTransform: 'none',
                fontWeight: 400,
                minHeight: 32,
              },
              '& .MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3,
              },
            }}
            value={activeTab}
            onChange={handleTabChange}
          >
            <Tab
              label="Clinical clients"
              {...a11yProps(0)}
              value={AdminPageTabs.ClientTab}
              data-testid="clinicalClientsTab"
            />
            <Tab
              label="Payors"
              {...a11yProps(0)}
              value={AdminPageTabs.PayorTab}
              data-testid="payorsTab"
            />
            {areUserRolesIn(user?.roles, [
              Roles.Admin,
              Roles.OnboardingCoordinator,
              Roles.Biller,
              Roles.ClientSuccessManager,
            ]) && (
              <Tab
                label="Clinical providers"
                {...a11yProps(0)}
                value={AdminPageTabs.ProviderTab}
                data-testid="clinicalProvidersTab"
              />
            )}
            {areUserRolesIn(user?.roles, [Roles.Admin, Roles.OnboardingCoordinator]) && (
              <Tab
                label="Facilities"
                {...a11yProps(0)}
                value={AdminPageTabs.BFTab}
                data-testid="FacilitiesTab"
              />
            )}
            {areUserRolesIn(user?.roles, [Roles.Admin]) && (
              <Tab
                label="Goal Bank"
                {...a11yProps(0)}
                value={AdminPageTabs.GoalBankTab}
                data-testid="goalBankTab"
              />
            )}
            {areUserRolesIn(user?.roles, [Roles.Admin]) && (
              <Tab
                label="Cohorts"
                {...a11yProps(0)}
                value={AdminPageTabs.CohortsTab}
                data-testid="cohortsTab"
              />
            )}
            {areUserRolesIn(user?.roles, [Roles.Admin]) && (
              <Tab
                label="Email Reports"
                {...a11yProps(0)}
                value={AdminPageTabs.EmailReportsTab}
                data-testid="emailReportsTab"
              />
            )}
            {areUserRolesIn(user?.roles, [Roles.Admin]) && (
              <Tab
                label="Feature Flags"
                value={AdminPageTabs.FeatureFlagsTab}
                data-testid="featureFlagsTab"
                {...a11yProps(0)}
              />
            )}
            {areUserRolesIn(user?.roles, [
              Roles.Admin,
              Roles.OnboardingCoordinator,
              Roles.ClientSuccessManager,
            ]) && (
              <Tab
                label="All clients"
                {...a11yProps(0)}
                value={AdminPageTabs.AllClientsTab}
                data-testid="allClientsTab"
              />
            )}
            {areUserRolesIn(user?.roles, [
              Roles.Admin,
              Roles.OnboardingCoordinator,
              Roles.ClientSuccessManager,
            ]) && (
              <Tab
                label="All providers"
                {...a11yProps(0)}
                value={AdminPageTabs.AllProvidersTab}
                data-testid="allProvidersTab"
              />
            )}
          </Tabs>

          <TabPanel value={activeTab} index={AdminPageTabs.ClientTab}>
            {clientLoading ? (
              <AdminTableLoader />
            ) : (
              <FHSimpleTable
                data={getSortedItemsBasedOnId(transformAdminTableData(clients))}
                columnDefinitions={getClientColumns(false)}
                columnOptions={clientOptions}
              />
            )}
          </TabPanel>
          <TabPanel value={activeTab} index={AdminPageTabs.PayorTab}>
            {payorsLoading ? (
              <AdminTableLoader />
            ) : (
              <FHSimpleTable
                data={getSortedItemsBasedOnId(payorsList)}
                columnDefinitions={payorColumns}
                columnOptions={payorOptions}
              />
            )}
          </TabPanel>
          <TabPanel value={activeTab} index={AdminPageTabs.ProviderTab}>
            {providerLoading ? (
              <AdminTableLoader />
            ) : (
              <FHSimpleTable
                data={getSortedItemsBasedOnId(transformAdminTableData(enrichedProviders))}
                columnDefinitions={getProviderColumns(false)}
                columnOptions={providerOptions}
              />
            )}
          </TabPanel>
          <TabPanel value={activeTab} index={AdminPageTabs.BFTab}>
            {facilityLoading ? (
              <AdminTableLoader />
            ) : (
              <FHSimpleTable
                data={getSortedItemsBasedOnId(facilities)}
                columnDefinitions={facilityColumns}
                columnOptions={dataTableOptions}
              />
            )}
          </TabPanel>
          <TabPanel value={activeTab} index={AdminPageTabs.GoalBankTab}>
            {goalNamesLoading ? (
              <AdminTableLoader />
            ) : (
              <FHSimpleTable
                data={getSortedItemsBasedOnId(enrichedGoalTemplets)}
                columnDefinitions={goalTemplateColumns}
                columnOptions={goalTemplateTableOptions}
              />
            )}
          </TabPanel>
          <TabPanel value={activeTab} index={AdminPageTabs.CohortsTab}>
            {cohortsLoading ? (
              <AdminTableLoader />
            ) : (
              <FHSimpleTable
                data={getSortedItemsBasedOnId(updatedCohorts)}
                columnDefinitions={cohortColumns}
                columnOptions={cohortTableOptions}
              />
            )}
          </TabPanel>
          {areUserRolesIn(user?.roles, [Roles.Admin]) && (
            <TabPanel value={activeTab} index={AdminPageTabs.EmailReportsTab}>
              {emailReportsLoading ? (
                <AdminTableLoader />
              ) : (
                <FHSimpleTable
                  data={getSortedItemsBasedOnId(emailReports)}
                  columnDefinitions={emailReportsColumns}
                  columnOptions={emailReportsTableOptions}
                />
              )}
            </TabPanel>
          )}
          <TabPanel value={activeTab} index={AdminPageTabs.FeatureFlagsTab}>
            {areUserRolesIn(user?.roles, [Roles.Admin]) && <FeatureFlagsTab />}
          </TabPanel>
          <TabPanel value={activeTab} index={AdminPageTabs.AllClientsTab}>
            {clientLoading ? (
              <AdminTableLoader />
            ) : (
              <FHSimpleTable
                data={getSortedItemsBasedOnId(transformAdminTableData(allClientsList))}
                columnDefinitions={getClientColumns()}
                columnOptions={clientOptions}
              />
            )}
          </TabPanel>
          <TabPanel value={activeTab} index={AdminPageTabs.AllProvidersTab}>
            {providerLoading ? (
              <AdminTableLoader />
            ) : (
              <FHSimpleTable
                data={getSortedItemsBasedOnId(transformAdminTableData(enrichedAllProviderList))}
                columnDefinitions={getProviderColumns()}
                columnOptions={providerOptions}
              />
            )}
          </TabPanel>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default Dashboard;
